import { ReactNode, ReactElement, useState } from 'react';
import { createPortal } from 'react-dom';

import { HeadCSP } from './HeadCSP';
import { HeadStyle } from './HeadStyle';

export const defaultTestId = 'secure-iframe';

type Props = {
  children: ReactNode;
  className?: string; // allows styling of iframe via styled-components
  HeadContent?: ReactElement;
  testId?: string;
};

/* Secure Iframe
 * Works well with the side-by-side editor
 * - rendering content with portal(s) does not trigger subsequent onLoad events when the content
 *   changes. This means that the iframe does not reset and scroll to the top while changes are made
 *   and does not create a flicker (seen in images) each time the contents are re-rendered
 *
 * This component simplifies the wrapping of potentially dangerous content (partial html: excludes
 * html, head, and body tags) with an iframe.
 *
 * This component is suitable for passing a child with dangerouslySetInnerHTML. Emample:
 *   <Iframe>
 *     <Preview dangerouslySetInnerHTML={{ __html: removeScript(value) }} />
 *   </Iframe>
 *
 * Note: In the interest of being thorough, it is recommended that you first sanitize the HTML
 *       content with the removeScript() utility from 'utils/script'.
 *
 * Security considerations:
 * - the iframe itself is sandboxed ('allow-same-origin' is necessary for React to manipulate the
 *   contents in real time)
 * - a CSP is injected into the header of the iframe's content, allowing only the resources that are
 *   absolutely necessary for our members content to render correctly
 * - the html used for the source of the iframe's body is sanitized with removeScript()
 *   prior to rendering
 *
 * Styling:
 * This component may be styled normally with styled-components
 */

export const Iframe = ({
  children,
  className,
  HeadContent = <HeadStyle />,
  testId = defaultTestId,
}: Props) => {
  const [previewBodyNode, setPreviewBodyNode] = useState<HTMLElement>();
  const [previewHeadNode, setPreviewHeadNode] = useState<HTMLElement>();

  /* Important: Firefox resets the iframe content after it receives the load event, so setting the
   * content on ComponentDidMount (useEffect with empty dependency array) will be overwritten
   * resulting in an empty iframe. To fix this behavior, set the nodes and portals following the
   * onLoad callback rather than ComponentDidMount. Also, it is important that the node references
   * are set with state in order to trigger a re-render to activate the portals (setting them via
   * useRef will not trigger a rerender which will result in an empty iframe until a rerender is
   * triggered). Finally, to avoid potential issues in Chrome, render the portals outside of the
   * iframe (they may, in some environments, get removed if rendered inside of the iframe tags) */

  const handleLoad = (e: any) => {
    // manually set references following render/load
    // note: changing the contents via the portal(s) does not trigger subsequent onLoad events
    setPreviewBodyNode(e.target.contentDocument?.body);
    setPreviewHeadNode(e.target.contentDocument?.head);
  };

  return (
    <>
      <iframe
        className={className}
        data-test={testId}
        onLoad={handleLoad}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" // allow-same-origin: allows us to access the iframe's innards via JS; allow-popups: Allows new windows/tabs to be opened from links; allow-popups-to-escape-sandbox: allows a sandboxed document to open new windows without forcing the sandboxing flags upon them
        srcDoc={`<!DOCTYPE html>`} // give Safari a starting point, otherwise it will render and empty iframe
      />
      {previewHeadNode &&
        createPortal(
          <>
            <HeadCSP />
            {HeadContent}
          </>,
          previewHeadNode,
        )}
      {previewBodyNode && createPortal(children, previewBodyNode)}
    </>
  );
};

export default Iframe;
